// Jogo dos emojis — the guessing side (IDEA-025 v3).
//
// The game used to give three tries and score 5/3/1 by which one landed. It now
// gives a clock and as many tries as you like: a wrong guess costs seconds, not
// a life, and a hint reveals the next letter for a flat ten-second penalty. The
// round is then scored exactly like Memória and Shikaku — add up your time,
// faster wins.
//
// Everything here is pure so the API and the browser agree on the same three
// things: which letters a hint has uncovered, how many hints a word may take,
// and what a round's time adds up to. The API is the one that *enforces* the
// cap and builds the mask, because the answer must never reach the guesser (see
// services/games.ts) — but the UI needs the same rules to draw the dashes.

import { HINT_PENALTY_MS } from "./validation.js";

/**
 * Characters a hint can uncover: letters and digits, accents included.
 *
 * Everything else — spaces, hyphens, apostrophes, "&" — is shown from the
 * start. It gives nothing away (`comparisonKey` strips all of it before
 * comparing, so "toy-story" and "toystory" are the same guess either way) and
 * seeing the shape of "O REI LEÃO" is most of what makes the dashes readable.
 */
const OCULTAVEL = /[\p{L}\p{N}]/u;

/** Uppercase, always — easier to read at a glance, and one less way to be wrong. */
export function toDisplayWord(palavra: string): string {
  return palavra.toUpperCase();
}

/** Indexes (into the display word) that a hint could uncover, left to right. */
export function hideablePositions(palavra: string): number[] {
  const texto = toDisplayWord(palavra);
  const posicoes: number[] = [];
  for (let i = 0; i < texto.length; i++) {
    if (OCULTAVEL.test(texto[i])) posicoes.push(i);
  }
  return posicoes;
}

/**
 * How many hints a word allows: every letter but the last one.
 *
 * The final guess is always yours. Without this you could uncover the whole
 * answer and "guess" by reading it, which is not a guess — and it would leave
 * the old game's saddest state (a word nobody ever actually got) alive under a
 * new name.
 */
export function maxHints(palavra: string): number {
  return Math.max(0, hideablePositions(palavra).length - 1);
}

/**
 * The word as the guesser may see it: uncovered letters in place, dashes for
 * the rest, spaces and punctuation always visible.
 *
 *   maskWord("O Rei Leão", 0) === "O _ _ _  _ _ _ _"  (conceptually)
 *
 * Rendered as one character per slot by the UI. Hints uncover left to right —
 * predictable, and the front of a word is what makes it recognisable.
 */
export function maskWord(palavra: string, pistas: number): string {
  const texto = toDisplayWord(palavra);
  const posicoes = hideablePositions(palavra);
  const descobertas = new Set(posicoes.slice(0, Math.max(0, pistas)));

  return [...texto]
    .map((ch, i) => (OCULTAVEL.test(ch) && !descobertas.has(i) ? "_" : ch))
    .join("");
}

/** A word's contribution to your round time: what the clock said, plus the hints. */
export function wordTime(word: { tempo_ms: number | null; pistas: number }): number {
  return (word.tempo_ms ?? 0) + word.pistas * HINT_PENALTY_MS;
}

/**
 * Turns a finished round's words into the same shape the timed games score.
 *
 * A word is composed by one of us and guessed by the other, so your time for a
 * round is the sum of the words you were *given* — the ones you did not write.
 * Feed the result straight to `timedRoundPoints()`; the emoji game and the two
 * board games then score through one function, which is the only reason a
 * combined "Tudo" total means anything.
 *
 * Returns [] unless exactly two people composed and every word is solved —
 * a half-played round scores nothing, same as a half-played Memória board.
 *
 * Also [] for a round played before the clock existed. Those words have no
 * `tempo_ms` at all, so both totals come out 0, and a 0–0 round is a dead heat:
 * every single round from the old game would hand out a point to each of us.
 * A few dozen of those and the scoreboard is pinned level, with new rounds
 * moving it one at a time against a wall of phantom draws. They were never
 * races — there is no "faster" to find in them — so they sit the scoring out
 * and stay exactly where they belong, in the history.
 */
export function emojiRoundResults(
  words: {
    autor_email: string;
    tempo_ms: number | null;
    pistas: number;
    acertou_na: number | null;
    desistiu?: boolean;
  }[],
): { jogador_email: string; tempo_ms: number; desistiu: boolean }[] {
  const autores = [...new Set(words.map((w) => w.autor_email))];
  if (autores.length !== 2) return [];
  // A word is closed by being guessed OR by being given up on.
  if (words.some((w) => w.acertou_na === null && !w.desistiu)) return [];
  if (words.every((w) => w.tempo_ms === null)) return []; // anterior ao cronómetro

  return autores.map((email) => {
    // The words this person guessed are the ones the *other* one wrote.
    const dadas = words.filter((w) => w.autor_email !== email);
    return {
      jogador_email: email,
      tempo_ms: dadas.reduce((total, w) => total + wordTime(w), 0),
      // Giving up on a word you were given is giving up on the round. It loses
      // whatever the clock says — `timedRoundPoints` rules it out of the race
      // rather than treating it as a very fast time, which is what walking away
      // after four seconds would otherwise look like.
      desistiu: dadas.some((w) => Boolean(w.desistiu)),
    };
  });
}

export { HINT_PENALTY_MS };
